/**
 * Main Application Component
 * PDF to LaTeX Multi-Role AI Processor
 */

import React, { useState } from 'react';
import { PDFUploader } from './components/PDFUploader';
import { RoleConfigPanel } from './components/RoleConfigPanel';
import { ProgressTracker } from './components/ProgressTracker';
import { LaTeXPreview } from './components/LaTeXPreview';
import { LogViewer } from './components/LogViewer';
import { ServerHealthCheck } from './components/ServerHealthCheck';
import { ExtractedTextViewer } from './components/ExtractedTextViewer';
import { useProcessingStore } from './store/useProcessingStore';
import { useConfigStore } from './store/useConfigStore';
import { ProcessingStep } from '@shared/types';
import { Settings, Upload, Zap, FileText, FileCode } from 'lucide-react';

type ActiveTab = 'upload' | 'config' | 'extracted' | 'process' | 'result';

function App() {
  const { state, result } = useProcessingStore();
  const { isConfigured } = useConfigStore();
  const [activeTab, setActiveTab] = useState<ActiveTab>('upload');

  // Auto-switch tabs based on processing state
  React.useEffect(() => {
    if (!state) return;

    // Show extracted text when extraction is complete
    if (state.currentStep === ProcessingStep.EXTRACTING_TEXT && state.progress >= 20) {
      setActiveTab('extracted');
    }
    // Show process tab when interpretation starts
    else if (state.currentStep === ProcessingStep.INTERPRETING ||
        state.currentStep === ProcessingStep.GENERATING_SOLUTIONS ||
        state.currentStep === ProcessingStep.LOGIC_CHECKING ||
        state.currentStep === ProcessingStep.VALIDATING ||
        state.currentStep === ProcessingStep.GENERATING_LATEX) {
      setActiveTab('process');
    }
    // Show result when completed
    else if (state.currentStep === ProcessingStep.COMPLETED && result) {
      setActiveTab('result');
    }
  }, [state, result]);

  const tabs = [
    {
      id: 'upload' as ActiveTab,
      label: '上传文件',
      icon: Upload,
      enabled: true,
    },
    {
      id: 'config' as ActiveTab,
      label: '配置AI',
      icon: Settings,
      enabled: true,
    },
    {
      id: 'extracted' as ActiveTab,
      label: '提取内容',
      icon: FileCode,
      enabled: !!state && state.currentStep !== ProcessingStep.IDLE && state.currentStep !== ProcessingStep.UPLOADING,
    },
    {
      id: 'process' as ActiveTab,
      label: '处理进度',
      icon: Zap,
      enabled: !!state && state.currentStep !== ProcessingStep.IDLE,
    },
    {
      id: 'result' as ActiveTab,
      label: 'LaTeX结果',
      icon: FileText,
      enabled: !!result,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                PDF to LaTeX AI Processor
              </h1>
              <p className="mt-1 text-gray-600">
                多角色 AI 协作系统 - 基于 LOCA-R 架构
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`
                px-4 py-2 rounded-full text-sm font-medium
                ${isConfigured()
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'}
              `}>
                {isConfigured() ? '✓ AI 已配置' : '⚠ 请配置 AI'}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => tab.enabled && setActiveTab(tab.id)}
                  disabled={!tab.enabled}
                  className={`
                    flex items-center space-x-2 py-4 px-3 border-b-2 font-medium text-sm transition-all
                    ${isActive
                      ? 'border-blue-600 text-blue-600'
                      : tab.enabled
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      : 'border-transparent text-gray-300 cursor-not-allowed'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                上传 PDF 文档
              </h2>
              <p className="text-gray-600">
                支持数学题目、物理题目、化学题目等各类学科文档
              </p>
            </div>
            <ServerHealthCheck />
            <PDFUploader />
            {!isConfigured() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <p className="text-yellow-800">
                  💡 提示：开始处理前请先
                  <button
                    onClick={() => setActiveTab('config')}
                    className="text-blue-600 hover:text-blue-700 underline font-medium mx-1"
                  >
                    配置 AI 角色
                  </button>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                配置四个 AI 角色
              </h2>
              <p className="text-gray-600">
                解读者、逻辑检查者、解析制造者、结果验证者
              </p>
            </div>
            <RoleConfigPanel />
          </div>
        )}

        {/* Extracted Text Tab */}
        {activeTab === 'extracted' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                提取的内容
              </h2>
              <p className="text-gray-600">
                从 PDF 中提取的原始 LaTeX/文本内容
              </p>
            </div>
            <ExtractedTextViewer />
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-800">
                ✅ 内容提取完成！AI 将基于此内容进行解析和处理
              </p>
              <p className="text-sm text-green-600 mt-2">
                处理将自动继续，您可以切换到"处理进度"标签查看详情
              </p>
            </div>
          </div>
        )}

        {/* Process Tab */}
        {activeTab === 'process' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                处理进度
              </h2>
              <p className="text-gray-600">
                四个 AI 角色正在协同工作
              </p>
            </div>
            <ServerHealthCheck />
            <ProgressTracker />
            <LogViewer />
          </div>
        )}

        {/* Result Tab */}
        {activeTab === 'result' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                LaTeX 文档
              </h2>
              <p className="text-gray-600">
                处理完成！可以预览、复制或下载生成的 LaTeX 文档
              </p>
            </div>
            <LaTeXPreview />
            <div className="text-center">
              <button
                onClick={() => {
                  useProcessingStore.getState().reset();
                  setActiveTab('upload');
                }}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                处理新文档
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-gray-600 text-sm">
            <p>Powered by Cloudflare Pages + Workers</p>
            <p className="mt-1">多角色 AI 协作系统 © 2024</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
